import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Navbar from '../../components/shared-menu/Navbar';
import { surface } from '../../theme';

const features = [
  {
    title: 'Earnings Overview',
    description: 'Track income, loan amounts and payouts.',
    icon: '/images/earnings.svg',
  },
  {
    title: 'Manage Applications',
    description: 'Refer customers & monitor loan status.',
    icon: '/images/applications.svg',
  },
  {
    title: 'Notifications & Help',
    description: 'Get updates & access support.',
    icon: '/images/notifications.svg',
  },
];

/**
 * Shared shell for the three sign-in pages: navbar, the 70/30 split with the
 * marketing hero on the left, and the footer. Children are the sign-in form,
 * centred in the right panel.
 */
export default function SignInLayout({ children }) {
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="start"
      height="100%"
    >
      <Navbar showLogout={false} isTransparent />

      <Box
        flex={1}
        display="grid"
        sx={{
          gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' },
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{
            backgroundColor: surface.raised,
            px: { xs: 3, md: 8 },
            py: { xs: 5, md: 8 },
          }}
        >
          <Box width="100%" maxWidth={900} mx="auto">
            <Typography
              variant="h4"
              component="h1"
              color="primary"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.75rem', md: '2rem', lg: '2.25rem' } }}
            >
              Welcome to the Reseller and Partner Portal
            </Typography>

            <Typography
              variant="body1"
              color="textSecondary"
              mt={1}
            >
              Use this portal to explore these features and boost your sales.
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap-reverse"
              gap={4}
              mt={5}
            >
              <Box
                component="img"
                src="/images/signinintro.svg"
                alt="Reseller Sign in representation"
                sx={{ width: { xs: 240, md: 280, lg: 360 }, maxWidth: '100%' }}
              />

              <Box display="flex" flexDirection="column" gap={3}>
                {features.map((feature) => (
                  <Box
                    key={feature.title}
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <img src={feature.icon} alt={feature.title} />

                    <Box maxWidth={280} textAlign="left">
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color="primary"
                      >
                        {feature.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{
            order: { xs: -1, md: 0 },
            backgroundColor: surface.default,
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 8 },
          }}
        >
          <Box
            width="100%"
            maxWidth={400}
            mx="auto"
            display="flex"
            flexDirection="column"
            gap={2.5}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Typography p={2} textAlign="center">
        {t('footer.rights', {
          defaultValue:
            'All rights Reserved by Nipun Projects and Finance Private Limited. Powered By Opticap Holding India Pvt Ltd',
        })}
      </Typography>
    </Box>
  );
}
